import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useService } from '@/app/di';
import { PATIENTS_SERVICE } from '../tokens';
import { MedicalConditionsService, AllergiesService } from '../infrastructure/patients.service';
import { useFeatureFlag } from '@/features/settings';
import { QUERY_STALE_MS } from '@/shared/api/query-stale-time';
import type {
  Patient,
  PatientListParams,
  PatientContact,
  PatientMedicalCondition,
  PatientAllergy,
  PatientVital,
  PatientClinicalFlag,
  ClinicalFlagTemplate,
  MedicalCondition,
  Allergy,
  PatientListResponse,
  VitalsHistoryResponse,
  Doctor,
} from '../domain/types';

const conditionsService = new MedicalConditionsService();
const allergiesService = new AllergiesService();

export const patientKeys = {
  all: ['patients'] as const,
  lists: () => [...patientKeys.all, 'list'] as const,
  list: (params: PatientListParams) => [...patientKeys.lists(), params] as const,
  details: () => [...patientKeys.all, 'detail'] as const,
  detail: (id: string) => [...patientKeys.details(), id] as const,
  contacts: (id: string) => [...patientKeys.all, 'contacts', id] as const,
  medicalConditions: (id: string) => [...patientKeys.all, 'medical-conditions', id] as const,
  allergies: (id: string) => [...patientKeys.all, 'allergies', id] as const,
  vitalsLatest: (id: string) => [...patientKeys.all, 'vitals-latest', id] as const,
  vitalsHistory: (id: string) => [...patientKeys.all, 'vitals-history', id] as const,
  clinicalFlags: (id: string) => [...patientKeys.all, 'clinical-flags', id] as const,
  doctors: () => [...patientKeys.all, 'doctors'] as const,
};

export const conditionKeys = {
  all: ['medical-conditions'] as const,
  list: (orgId: string) => [...conditionKeys.all, 'list', orgId] as const,
};

export const allergyKeys = {
  all: ['allergies'] as const,
  list: (orgId: string) => [...allergyKeys.all, 'list', orgId] as const,
};

import type {
  IPatientRepository,
  CreatePatientInput,
  UpdatePatientInput,
  UpdatePatientContactInput,
} from '../domain/repositories/patient.repository';

function useRepo(): IPatientRepository {
  return useService(PATIENTS_SERVICE);
}

export function usePatientList(
  params: PatientListParams,
  options?: { enabled?: boolean; keepPrevious?: boolean },
) {
  const repo = useRepo();
  return useQuery({
    queryKey: patientKeys.list(params),
    queryFn: () => repo.list(params),
    staleTime: QUERY_STALE_MS.HOT,
    enabled: options?.enabled ?? true,
    placeholderData: options?.keepPrevious ? keepPreviousData : undefined,
  });
}

export function usePatient(id: string | undefined) {
  const repo = useRepo();
  return useQuery({
    queryKey: patientKeys.detail(id!),
    queryFn: () => repo.getById(id!),
    enabled: !!id,
    staleTime: QUERY_STALE_MS.HOT,
  });
}

export function usePatientContacts(patientId: string | undefined) {
  const repo = useRepo();
  return useQuery({
    queryKey: patientKeys.contacts(patientId!),
    queryFn: () => repo.getContacts(patientId!),
    enabled: !!patientId,
    staleTime: QUERY_STALE_MS.HOT,
  });
}

export function usePatientMedicalConditions(patientId: string | undefined) {
  const repo = useRepo();
  return useQuery({
    queryKey: patientKeys.medicalConditions(patientId!),
    queryFn: () => repo.getMedicalConditions(patientId!),
    enabled: !!patientId,
    staleTime: QUERY_STALE_MS.HOT,
  });
}

export function usePatientAllergies(patientId: string | undefined) {
  const repo = useRepo();
  return useQuery({
    queryKey: patientKeys.allergies(patientId!),
    queryFn: () => repo.getAllergies(patientId!),
    enabled: !!patientId,
    staleTime: QUERY_STALE_MS.HOT,
  });
}

export function useSetPatientMedicalConditions() {
  const queryClient = useQueryClient();
  const repo = useRepo();
  return useMutation<
    void,
    Error,
    {
      patientId: string;
      input: { conditions: { name: string; notes?: string }[] };
    }
  >({
    mutationFn: ({ patientId, input }) => repo.setMedicalConditions(patientId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: patientKeys.medicalConditions(variables.patientId),
      });
    },
  });
}

export function useSetPatientAllergies() {
  const queryClient = useQueryClient();
  const repo = useRepo();
  return useMutation<
    void,
    Error,
    {
      patientId: string;
      input: {
        allergies: { name: string; reactionType?: string; severity?: string; notes?: string }[];
      };
    }
  >({
    mutationFn: ({ patientId, input }) => repo.setAllergies(patientId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: patientKeys.allergies(variables.patientId) });
    },
  });
}

export function usePatientLatestVitals(patientId: string | undefined) {
  const repo = useRepo();
  const { data: vitalsEnabled } = useFeatureFlag('vitals_and_flags');
  return useQuery({
    queryKey: patientKeys.vitalsLatest(patientId!),
    queryFn: () => repo.getLatestVitals(patientId!),
    enabled: !!patientId && vitalsEnabled === true,
    staleTime: QUERY_STALE_MS.HOT,
    retry: (failureCount, error) => {
      if (failureCount >= 2) return false;
      if (
        error instanceof Error &&
        (error.message.includes('Feature not enabled') ||
          error.message.includes('is not enabled'))
      ) {
        return false;
      }
      return true;
    },
  });
}

export function usePatientClinicalFlags(patientId: string | undefined) {
  const repo = useRepo();
  const { data: vitalsEnabled } = useFeatureFlag('vitals_and_flags');
  return useQuery({
    queryKey: patientKeys.clinicalFlags(patientId!),
    queryFn: () => repo.getClinicalFlags(patientId!),
    enabled: !!patientId && vitalsEnabled === true,
    staleTime: QUERY_STALE_MS.HOT,
    retry: (failureCount, error) => {
      if (failureCount >= 2) return false;
      if (
        error instanceof Error &&
        (error.message.includes('Feature not enabled') ||
          error.message.includes('is not enabled'))
      ) {
        return false;
      }
      return true;
    },
  });
}

export function useCreatePatient() {
  const queryClient = useQueryClient();
  const repo = useRepo();
  return useMutation<Patient, Error, { input: CreatePatientInput; key?: string }>({
    mutationFn: ({ input, key }) => repo.create(input, key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
    },
  });
}

export function useUpdatePatient() {
  const queryClient = useQueryClient();
  const repo = useRepo();
  return useMutation<Patient, Error, { id: string; input: UpdatePatientInput }>({
    mutationFn: ({ id, input }) => repo.update(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
      queryClient.invalidateQueries({ queryKey: patientKeys.details() });
    },
  });
}

export function useDeletePatient() {
  const queryClient = useQueryClient();
  const repo = useRepo();
  return useMutation<void, Error, string>({
    mutationFn: (id) => repo.remove(id),
    onSuccess: (_data, id) => {
      queryClient.setQueriesData<PatientListResponse>(
        { queryKey: patientKeys.lists() },
        (prev) => {
          if (!prev) return prev;
          const data = prev.data.filter((p) => p.id !== id);
          if (data.length === prev.data.length) return prev;
          return {
            ...prev,
            data,
            total: Math.max(0, prev.total - 1),
          };
        },
      );
      queryClient.removeQueries({ queryKey: patientKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: patientKeys.lists() });
    },
  });
}

export function useUpdatePatientContacts() {
  const queryClient = useQueryClient();
  const repo = useRepo();
  return useMutation<
    PatientContact,
    Error,
    {
      patientId: string;
      input: UpdatePatientContactInput;
    }
  >({
    mutationFn: ({ patientId, input }) => repo.updateContacts(patientId, input),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: patientKeys.contacts(variables.patientId) });
    },
  });
}

export function useOrgDoctors() {
  const repo = useRepo();
  return useQuery({
    queryKey: patientKeys.doctors(),
    queryFn: () => repo.getDoctors(),
    staleTime: QUERY_STALE_MS.CATALOG,
  });
}

export function useOrgConditions() {
  return useQuery({
    queryKey: conditionKeys.list('_'),
    queryFn: () => conditionsService.list(),
    staleTime: QUERY_STALE_MS.CATALOG,
  });
}

export function useCreateCondition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string }) => conditionsService.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: conditionKeys.all }),
  });
}

export function useUpdateCondition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: { name?: string } }) =>
      conditionsService.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: conditionKeys.all }),
  });
}

export function useDeactivateCondition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => conditionsService.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: conditionKeys.all }),
  });
}

export function useSeedConditions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => conditionsService.seed(),
    onSuccess: () => qc.invalidateQueries({ queryKey: conditionKeys.all }),
  });
}

export function useOrgAllergies() {
  return useQuery({
    queryKey: allergyKeys.list('_'),
    queryFn: () => allergiesService.list(),
    staleTime: QUERY_STALE_MS.CATALOG,
  });
}

export function useCreateAllergy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string }) => allergiesService.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: allergyKeys.all }),
  });
}

export function useUpdateAllergy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: { name?: string } }) =>
      allergiesService.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: allergyKeys.all }),
  });
}

export function useDeactivateAllergy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => allergiesService.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: allergyKeys.all }),
  });
}

export function useSeedAllergies() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => allergiesService.seed(),
    onSuccess: () => qc.invalidateQueries({ queryKey: allergyKeys.all }),
  });
}

export type {
  Patient,
  PatientListParams,
  PatientListResponse,
  CreatePatientInput,
  UpdatePatientInput,
  PatientContact,
  PatientMedicalCondition,
  MedicalCondition,
  PatientAllergy,
  Allergy,
  PatientVital,
  VitalsHistoryResponse,
  PatientClinicalFlag,
  ClinicalFlagTemplate,
  Doctor,
};
